/**
 * Pagina PUBBLICA (no auth) per accettazione online del preventivo via QR code.
 * Path: /p/preventivo/:id
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, FileText, Building2, Calendar, Hash } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SignaturePad } from '../components/SignaturePad';
import { toast, Toaster } from 'sonner';
import { parseApiError } from '../lib/parseApiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEur = (n) => {
  const num = Number(n || 0);
  return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function PortaleAccettazionePage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [firma, setFirma] = useState(null);
  const [nomeFirmatario, setNomeFirmatario] = useState('');
  const [noteCliente, setNoteCliente] = useState('');
  const [accettato, setAccettato] = useState(false);

  const fetchPreventivo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/preventivi/${id}`);
      setDoc(res.data);
      if (res.data.stato_accettazione === 'accettato') setAccettato(true);
    } catch (e) {
      setError(parseApiError(e, 'Preventivo non trovato'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchPreventivo(); }, [fetchPreventivo]);

  // Forza tema CHIARO sul portale pubblico (i clienti non devono vedere il dark mode)
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  const handleAccetta = async (e) => {
    e.preventDefault();
    if (!firma) { toast.error('Per favore firma il preventivo nello spazio sotto'); return; }
    if (!nomeFirmatario.trim()) { toast.error('Inserisci il nome del firmatario'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/preventivi/${id}/accetta`, {
        firma_base64: firma,
        nome_firmatario: nomeFirmatario.trim(),
        note_cliente: noteCliente.trim() || null,
      });
      setAccettato(true);
      toast.success('Preventivo accettato con successo!');
    } catch (e) {
      toast.error(parseApiError(e, 'Errore durante l\'accettazione'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Preventivo non disponibile</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 py-6 px-3 sm:py-10 sm:px-6">
      <Toaster position="top-center" richColors />
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <Card className="border-emerald-200 shadow-sm">
          <CardContent className="p-5 sm:p-7">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                {doc.azienda?.logo_base64 ? (
                  <img src={`data:image/png;base64,${doc.azienda.logo_base64}`} alt="logo" className="h-14 w-14 object-contain" />
                ) : (
                  <div className="h-14 w-14 rounded-md bg-emerald-100 flex items-center justify-center">
                    <Building2 className="h-7 w-7 text-emerald-600" />
                  </div>
                )}
                <div>
                  <p className="text-lg font-bold text-slate-900">{doc.azienda?.nome_azienda || 'Azienda'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {[doc.azienda?.telefono, doc.azienda?.email, doc.azienda?.sito_web].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-mono uppercase tracking-wider text-emerald-600">{doc.tipo === 'preventivo' ? 'Preventivo' : 'Offerta'}</p>
                <p className="text-lg font-black text-slate-900 mt-1">N. {doc.anno}/{String(doc.numero).padStart(3, '0')}</p>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 justify-end"><Calendar className="h-3 w-3" />{doc.data}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Saluto */}
        <div className="text-center px-2">
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Benvenuto, {doc.cliente?.ragione_sociale}</h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">Ecco il tuo preventivo. Puoi accettarlo direttamente da questa pagina firmando in fondo.</p>
        </div>

        {/* Voci preventivo */}
        <Card><CardContent className="p-5 sm:p-7">
          {doc.oggetto && (
            <div className="mb-4 pb-4 border-b border-slate-100">
              <p className="text-xs font-mono uppercase text-slate-500">Oggetto</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{doc.oggetto}</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-mono text-[11px] uppercase">Descrizione</th>
                  <th className="px-2 py-2 text-center font-mono text-[11px] uppercase">Qta</th>
                  <th className="px-2 py-2 text-right font-mono text-[11px] uppercase">Prezzo</th>
                  <th className="px-2 py-2 text-right font-mono text-[11px] uppercase">Totale</th>
                </tr>
              </thead>
              <tbody>
                {doc.voci.map((v, i) => (
                  v.is_titolo ? (
                    <tr key={`titolo-${i}`} className="bg-slate-100">
                      <td colSpan={4} className="px-3 py-2 font-bold uppercase tracking-wide text-slate-800 text-xs">{v.descrizione}</td>
                    </tr>
                  ) : (
                  <tr key={`${v.descrizione}-${i}`} className="border-b border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{v.descrizione}</td>
                    <td className="px-2 py-2 text-center text-slate-600 font-mono text-xs">{v.quantita} {v.unita_misura}</td>
                    <td className="px-2 py-2 text-right text-slate-600 font-mono text-xs">€ {fmtEur(v.prezzo_unitario)}</td>
                    <td className={`px-2 py-2 text-right font-mono text-xs ${v.escludi_totale ? 'text-slate-400 italic' : 'font-semibold text-slate-900'}`}>
                      € {fmtEur(v.totale)}{v.escludi_totale ? ' *' : ''}
                    </td>
                  </tr>
                  )
                ))}
                {doc.voci.some(v => v.escludi_totale) && (
                  <tr>
                    <td colSpan={4} className="px-3 py-1.5 text-[11px] italic text-slate-400">
                      * Prezzo a titolo informativo, non incluso nel totale del documento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
            <div className="flex justify-between text-slate-600"><span>Imponibile</span><span className="font-mono">€ {fmtEur(doc.subtotale)}</span></div>
            <div className="flex justify-between text-slate-600"><span>IVA {doc.iva_percentuale}%</span><span className="font-mono">€ {fmtEur(doc.iva_amount)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-200 text-xl font-black text-emerald-600">
              <span>TOTALE</span><span className="font-mono">€ {fmtEur(doc.totale)}</span>
            </div>
          </div>
          {doc.tipo_pagamento && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-mono uppercase text-slate-500">Modalità di pagamento</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{doc.tipo_pagamento}</p>
            </div>
          )}
        </CardContent></Card>

        {/* Form accettazione o messaggio già accettato */}
        {accettato ? (
          <Card className="border-emerald-500 bg-emerald-50 shadow-md">
            <CardContent className="p-7 text-center">
              <div className="h-14 w-14 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
                <Check className="h-8 w-8 text-white" strokeWidth={3} />
              </div>
              <h2 className="text-xl font-black text-emerald-800">Preventivo accettato</h2>
              <p className="text-sm text-emerald-700 mt-2">
                Grazie {doc.nome_firmatario || 'per la fiducia'}! Ti contatteremo a breve per i prossimi passi.
              </p>
              {doc.data_accettazione && (
                <p className="text-xs text-emerald-600 mt-3 font-mono">
                  Accettato il {new Date(doc.data_accettazione).toLocaleString('it-IT')}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-200 shadow-md">
            <CardContent className="p-5 sm:p-7">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Accetta il preventivo online</h2>
              <p className="text-sm text-slate-600 mb-5">Validità: {doc.validita_giorni} giorni dalla data di emissione</p>
              <form onSubmit={handleAccetta} className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome e cognome del firmatario <span className="text-red-500">*</span></Label>
                  <Input id="nome" value={nomeFirmatario} onChange={(e) => setNomeFirmatario(e.target.value)}
                    placeholder="Es. Mario Rossi" required data-testid="public-nome-firmatario" />
                </div>
                <div>
                  <Label htmlFor="note">Note / richieste (opzionale)</Label>
                  <Textarea id="note" value={noteCliente} onChange={(e) => setNoteCliente(e.target.value)}
                    placeholder="Eventuali precisazioni o richieste..." rows={2} data-testid="public-note-cliente" />
                </div>
                <SignaturePad value={firma} onChange={setFirma} label="Firma del cliente *" height={150} />
                <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-bold"
                  data-testid="public-accetta-btn">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Check className="h-5 w-5 mr-2" /> ACCETTA PREVENTIVO</>)}
                </Button>
                <p className="text-[11px] text-slate-500 text-center">
                  Cliccando accetti integralmente le condizioni del preventivo
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 pt-2 pb-6">
          <Hash className="h-3 w-3 inline mr-1" /> Ref: {doc.id}
        </p>
      </div>
    </div>
  );
}
